import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/buttons/Button";
import Breadcrumbs from "../../../components/BreadCrumbs";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import { toast } from "react-toastify";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import ShowModal from "../../../components/Hud/ShowModal";
import DoseTable from "../../../components/Hud/DoseTable";
import AddUpdateDose from "./new_add_update_dose";
import { pricesColorCoding } from "../../../constants/prices_colors_coding";
import { getNewHudPatientDoses } from "../../../services/new_clinic/new_hud_doses";
import { getNewTptMachines, startTptMachineDose } from "../../../services/new_clinic/new_tpt_machine";
import { getUserName } from "../../../utils/localStoarege";
import TptTreatmentPanel from "./tpt_treatment_panel";

const TPT_POLL_INTERVAL_MS = 10000;
const TPT_FAST_POLL_INTERVAL_MS = 3000;

const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    const message = data.error || data.detail || data.message || data.non_field_errors || data.dose_id;
    if (Array.isArray(message)) return message.join(" ");
    return message || fallback;
};

const NewPatientDoses = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const timerRef = useRef(null);

    const [patientRowData, setPatientRowData] = useState({});
    const [packageRowData, setPackageRowData] = useState({});
    const [tableData, setTableData] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingBg, setLoadingBg] = useState(false);

    // New TPT machine (only set when this center has one on the new server)
    const [tptMachine, setTptMachine] = useState(null);
    const [startingTreatment, setStartingTreatment] = useState(false);
    const [requestedDoseId, setRequestedDoseId] = useState(null);

    // Fetch data function
    const fetchData = async (packageId, needLoading = true) => {
        if (needLoading) {
            setLoading(true);
        } else {
            setLoadingBg(true);
        }

        try {
            let res = await getNewHudPatientDoses(packageId);
            if (res.status === 200) {
                setTableData(res.data);
            } else {
                toast.error("Unable to get packages");
            }
        } catch (error) {
            console.error("Error fetching doses:", error);
        } finally {
            setLoading(false);
            setLoadingBg(false);
        }
    };

    // Function to check the status of the last entry in tableData
    const checkLastEntryStatus = () => {
        if (tableData && tableData.results && tableData.results.length > 0) {
            const lastEntry = tableData.results[tableData.results.length - 1];

            if (lastEntry.status === "Completed") {
                toast.success("Last dose status is Completed. Stopping updates.");
                if (timerRef.current) {
                    clearInterval(timerRef.current); // Stop the timer
                    timerRef.current = null;
                }
                return true; // Status is completed
            } else {
                toast.info("Last dose is still in progress.");
                return false; // Status is not completed
            }
        }
        return false; // No data available to check
    };

    // Start periodic updates
    const startTimer = (packageId) => {
        // Clear any existing timer
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        fetchData(packageId);
        timerRef.current = setInterval(() => {
            fetchData(packageId, false);
            checkLastEntryStatus();
        }, 15000);
    };

    // Stop the timer
    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    // UseEffect to initialize data
    useEffect(() => {
        const data = location.state;
        if (data) {
            setPatientRowData(data.patient);
            setPackageRowData(data.package);
            startTimer(data.package.id); // Start periodic updates
        } else {
            toast.error("Error while looking for package doses");
            navigate(-1);
        }

        return () => {
            stopTimer(); // Cleanup timer on component unmount
        };
    }, [location.state]);

    const isNewTptCandidate = String(packageRowData.package_name || "").trim().toUpperCase() === "TPT";
    const doses = Array.isArray(tableData) ? tableData : (tableData?.results ?? []);
    const lastDose = doses.length > 0 ? doses[doses.length - 1] : null;
    const lastDoseCompleted = lastDose?.status === "Completed";
    const treatmentRequested = Boolean(lastDose) && requestedDoseId === lastDose.dose_id;

    // Check whether this center has a new TPT machine. No machine or a failed call = old machine flow.
    useEffect(() => {
        setTptMachine(null);
        setRequestedDoseId(null);
        if (!packageRowData.id || !isNewTptCandidate) return;

        const branchName = getUserName();
        if (!branchName || branchName === "null") return;

        let cancelled = false;
        getNewTptMachines(branchName)
            .then((res) => {
                const machine = res?.data?.results?.[0];
                if (!cancelled && machine?.id) setTptMachine(machine);
            })
            .catch((error) => {
                console.error("Error checking TPT machine:", error);
            });

        return () => {
            cancelled = true;
        };
    }, [packageRowData.id, isNewTptCandidate]);

    // Live status: after starting (or if the last dose is already running), refresh doses until it is Completed.
    const shouldPollTreatment =
        Boolean(tptMachine) &&
        Boolean(lastDose) &&
        !lastDoseCompleted &&
        (treatmentRequested || Boolean(lastDose.open_time));

    // Poll fast right after Start Treatment so "In Progress" shows as soon as the patient starts.
    const waitingForPatient = treatmentRequested && !lastDose?.open_time;
    const pollIntervalMs = waitingForPatient ? TPT_FAST_POLL_INTERVAL_MS : TPT_POLL_INTERVAL_MS;

    useEffect(() => {
        if (!shouldPollTreatment || !packageRowData.id) return;
        const intervalId = setInterval(() => {
            fetchData(packageRowData.id, false);
        }, pollIntervalMs);
        return () => clearInterval(intervalId);
    }, [shouldPollTreatment, pollIntervalMs, packageRowData.id]);

    // Browsers slow down timers in background tabs — refresh as soon as staff come back to this tab.
    useEffect(() => {
        if (!tptMachine || !packageRowData.id) return;
        const handleVisible = () => {
            if (document.visibilityState === "visible") fetchData(packageRowData.id, false);
        };
        document.addEventListener("visibilitychange", handleVisible);
        return () => document.removeEventListener("visibilitychange", handleVisible);
    }, [tptMachine, packageRowData.id]);

    const handleStartTreatment = async () => {
        if (!tptMachine || !lastDose || startingTreatment) return;
        setStartingTreatment(true);
        try {
            const res = await startTptMachineDose(tptMachine.id, lastDose.dose_id);
            if (res.status === 200) {
                toast.success("Machine unlocked — patient can start treatment");
                setRequestedDoseId(lastDose.dose_id);
                fetchData(packageRowData.id, false);
            }
        } catch (error) {
            const statusCode = error?.response?.status;
            if (statusCode === 409) {
                toast.warning("Machine is busy with another treatment");
            } else if (statusCode === 404) {
                toast.error("Dose not found");
            } else if (statusCode === 400) {
                toast.error(getApiErrorMessage(error, "Unable to start treatment"));
                // e.g. "Dose has already started." — refresh so the screen shows the current state
                fetchData(packageRowData.id, false);
            } else {
                toast.error(getApiErrorMessage(error, "Unable to start treatment, please try again."));
            }
        } finally {
            setStartingTreatment(false);
        }
    };

    const [modalIsOpenPackage, setIsOpenPackage] = useState(false);

    const closeAddPackageModal = () => {
        setIsOpenPackage(false);
    };

    const handlePackageOperation = () => {
        closeAddPackageModal();
        if (tableData.length > 14 && packageRowData.remaining_amount !== 0) {
            setTimeout(() => {
                toast.warning(`Please collect remaining amount of ₹${packageRowData.remaining_amount} for current package`);
            }, 1000);
        }
        fetchData(packageRowData.id);
    };

    const openModalAddPackage = () => {
        setIsOpenPackage(true);
    };

    const FREEZE_CUTOFF = new Date("2025-05-01");
    const isFrozen = packageRowData.date && new Date(packageRowData.date) < FREEZE_CUTOFF;
    const isDoseComplete = packageRowData.number_of_doses >= 18;
    const isTptPackage = String(packageRowData.package_name || "").toUpperCase().includes("TPT");

    // Payment stage checks — block after dose 10 if paid < 50%, block after dose 16 if not fully paid
    const dosesCount = packageRowData.number_of_doses ?? 0;
    const remaining = packageRowData.remaining_amount ?? 0;
    const totalAmount = packageRowData.amount ?? 0;
    const paidPercent = totalAmount > 0 ? ((totalAmount - remaining) / totalAmount) * 100 : 100;

    let paymentBlocked = false;
    let paymentBlockMessage = "";
    if (dosesCount >= 16 && remaining > 0) {
        paymentBlocked = true;
        paymentBlockMessage = `16 doses completed — clear the full remaining ₹${remaining} to add more doses`;
    } else if (dosesCount >= 10 && paidPercent < 50) {
        paymentBlocked = true;
        paymentBlockMessage = `10 doses completed — complete 50% payment to add more doses (remaining ₹${remaining})`;
    }

    return loading ? (
        <CustomLoading />
    ) : (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <div className="flex flex-row justify-between">
                <Breadcrumbs
                    items={[
                        { name: "Home", link: '/package-invoice' },
                        { name: "Patients", link: "/new-hud-patients" },
                        { name: "Patient Packages", link: (-1) },
                        { name: "Doses" },
                    ]}
                />
                {
                    loadingBg ? <p className="text-primary font-bold">Syncing...</p> : <></>
                }
            </div>
            {isFrozen && (
                <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-400 text-yellow-800 text-xs font-semibold px-4 py-2 rounded mb-3">
                    <span>&#9888;</span>
                    <span>This package is frozen — packages created before 01/05/2025 are read-only and no new doses can be added.</span>
                </div>
            )}
            {!isFrozen && paymentBlocked && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-400 text-red-700 text-xs font-semibold px-4 py-2 rounded mb-3">
                    <span>&#9888;</span>
                    <span>{paymentBlockMessage}</span>
                </div>
            )}
            <div className={`w-full flex-1 overflow-x-auto ${isFrozen ? "pointer-events-none select-none opacity-70" : ""}`}>
                <div className="flex flex-row justify-between items-center my-2 text-red-600">
                    <StyledHeadingText
                        text={`${patientRowData.name} (${"Age: " +
                            patientRowData.age ||
                            "DOB: " + patientRowData.dob
                            } | Gen: ${patientRowData.gender
                            }) || (${"Package: " + packageRowData.package_name
                            } | ${"Type: " + packageRowData.package_status})`}
                    />
                    <div className="relative group inline-block">
                        <Button
                            type="button"
                            className={`text-white px-4 py-2 rounded text-xs ${isFrozen || isDoseComplete || paymentBlocked ? "bg-gray-400 cursor-not-allowed opacity-60" : "bg-primary"}`}
                            onClick={() => { if (!isFrozen && !isDoseComplete && !paymentBlocked) openModalAddPackage(); }}
                            disabled={loading || isFrozen || isDoseComplete || paymentBlocked}
                        >
                            {loading ? "Adding..." : "Add Dose"}
                        </Button>
                        {isDoseComplete && !isFrozen && (
                            <span className="absolute bottom-full right-0 mb-1 whitespace-nowrap bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                All 18 doses completed
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-row items-center gap-2 mb-2">
                    <p className="font-bold text-xs">Pkg. Color: {pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.color ?? `₹${packageRowData.amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.short_hand ?? ``}</p>
                    <p className="font-bold text-xs"> | Rem. Color: {pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.color ?? `₹${packageRowData.remaining_amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.short_hand ?? ``}</p>
                </div>
                {loading ? (
                    <div className="flex w-full flex-row justify-center my-2">
                        <CustomLoading size={100} />
                    </div>
                ) : (
                    <DoseTable
                        data={tableData}
                        packageId={packageRowData.id}
                        needExtraOptions={false}
                        isNewClinic={true}
                        showLastDoseTreatment={Boolean(tptMachine)}
                        fetchData={() =>
                            fetchData(packageRowData.id)
                        }
                    />
                )}
                {!loading && tptMachine && lastDose && (
                    <TptTreatmentPanel
                        lastDose={lastDose}
                        doseNumber={doses.length}
                        machineName={tptMachine.name}
                        starting={startingTreatment}
                        waitingForMachine={treatmentRequested}
                        onStart={handleStartTreatment}
                        refreshing={loadingBg}
                        onRefresh={() => fetchData(packageRowData.id, false)}
                    />
                )}
            </div>

            <div className="flex gap-4 justify-end w-full mt-4">
                <Button
                    type="button"
                    className="bg-gray-500 text-white px-4 py-2 rounded"
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
            </div>
            <ShowModal
                contentLabel={"Add dose alert"}
                isOpen={modalIsOpenPackage}
                modalWidth={"50%"}
                onRequestClose={closeAddPackageModal}
                componentToPass={
                    <AddUpdateDose
                        packageId={packageRowData.id}
                        onDoseOperation={handlePackageOperation}
                        packageName={packageRowData.package_name}
                        requireOtp={isTptPackage}
                        phoneNumber={patientRowData.phone_number}
                        altNumber={patientRowData.alt_phone_number}
                    />
                }
            />
        </div>
    );
};

export default NewPatientDoses;
